import { PERSONNEL_TYPES, ROLES } from 'config/constants';

export type PersonnelAccountMode =
  | 'sponsorOrg'
  | 'researchSiteOrg'
  | 'personal'
  | 'none';

type AuthInfoForMode = {
  readonly groups?: ReadonlyArray<string>;
  readonly personnelTypes?: ReadonlyArray<string>;
  readonly currentOrganizationId?: string | null;
};

export type PersonnelAccountModeFlags = {
  readonly mode: PersonnelAccountMode;
  readonly isPersonnel: boolean;
  readonly isResearchSitePersonnel: boolean;
  readonly isSponsorPersonnel: boolean;
  readonly isResearchSitePersonalAccount: boolean;
  readonly isSponsorPersonalAccount: boolean;
  readonly isPersonalPersonnelHome: boolean;
  readonly isSponsorPersonnelRepresentativeProfile: boolean;
  readonly isResearchSitePersonnelRepresentativeProfile: boolean;
};

export const getPersonnelAccountMode = (
  authInfo: AuthInfoForMode,
  options?: { readonly isRepresentative?: boolean },
): PersonnelAccountModeFlags => {
  const { groups = [], personnelTypes = [], currentOrganizationId } = authInfo;
  const isRepresentative =
    options?.isRepresentative ?? groups.includes(ROLES.REPRESENTATIVE);

  const isPersonnel = groups.includes(ROLES.PERSONNEL);
  const isResearchSitesPersonnel = personnelTypes.includes(
    PERSONNEL_TYPES.RESEARCH_SITE,
  );
  const isSponsorsPersonnel = personnelTypes.includes(PERSONNEL_TYPES.SPONSOR);
  const hasResearchSitePersonnelGroup = groups.includes(
    ROLES.RESEARCH_SITE_PERSONNEL,
  );
  const hasSponsorPersonnelGroup = groups.includes(ROLES.SPONSOR_PERSONNEL);

  const hasCurrentOrganization = Boolean(currentOrganizationId);

  const isResearchSitePersonnel =
    isRepresentative &&
    isPersonnel &&
    isResearchSitesPersonnel &&
    hasResearchSitePersonnelGroup &&
    hasCurrentOrganization;
  const isSponsorPersonnel =
    isRepresentative &&
    isPersonnel &&
    isSponsorsPersonnel &&
    hasSponsorPersonnelGroup &&
    hasCurrentOrganization;

  const isResearchSitePersonalAccount =
    isRepresentative &&
    isPersonnel &&
    isResearchSitesPersonnel &&
    !hasResearchSitePersonnelGroup;

  const isSponsorPersonalAccount =
    isRepresentative &&
    isPersonnel &&
    isSponsorsPersonnel &&
    !hasSponsorPersonnelGroup;

  const isPersonalPersonnelHome =
    isPersonnel &&
    (isResearchSitesPersonnel || isSponsorsPersonnel) &&
    !hasCurrentOrganization;

  const isSponsorPersonnelRepresentativeProfile =
    isRepresentative &&
    isPersonnel &&
    isSponsorsPersonnel &&
    !groups.includes(ROLES.SPONSOR);

  const isResearchSitePersonnelRepresentativeProfile =
    isRepresentative &&
    isPersonnel &&
    isResearchSitesPersonnel &&
    !groups.includes(ROLES.RESEARCH_SITE);

  let mode: PersonnelAccountMode = 'none';

  if (isSponsorPersonnel) {
    mode = 'sponsorOrg';
  } else if (isResearchSitePersonnel) {
    mode = 'researchSiteOrg';
  } else if (isPersonalPersonnelHome) {
    mode = 'personal';
  }

  return {
    mode,
    isPersonnel,
    isResearchSitePersonnel,
    isSponsorPersonnel,
    isResearchSitePersonalAccount,
    isSponsorPersonalAccount,
    isPersonalPersonnelHome,
    isSponsorPersonnelRepresentativeProfile,
    isResearchSitePersonnelRepresentativeProfile,
  };
};
